// GoalsTab — live standing goals panel for the Agent Dashboard right rail
// Shows active goals with status badges, last-run time, and template tag.
// "+ New Goal" opens GoalCreateModal.

import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  listGoals, archiveGoal, formatGoalCadence, formatNextRun, type Goal,
  listSchedules, deleteSchedule, formatTaskCadence, formatTaskTime, type ScheduledTask,
} from '../../api/goals';
import { useCanWriteAgent } from '../../hooks/useCanWriteAgent';
import GoalCreateModal from './GoalCreateModal';
import styles from './GoalsTab.module.css';

const STATUS_LABEL: Record<string, string> = {
  active: 'Active',
  completed: 'Done',
  paused: 'Paused',
  abandoned: 'Abandoned',
};

function timeAgo(iso: string | null): string {
  if (!iso) return 'Never';
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 2) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

function GoalCard({ goal, canWrite, onArchive }: { goal: Goal; canWrite: boolean; onArchive: (id: string) => void }) {
  const navigate = useNavigate();
  const [confirming, setConfirming] = useState(false);
  const cadence = formatGoalCadence(goal);
  const nextRun = formatNextRun(goal);

  return (
    <div className={styles.card} onClick={() => navigate(`/agent/goals/${goal.id}`)}>
      <div className={styles.cardTop}>
        <span className={`${styles.statusBadge} ${styles[`status_${goal.status}`]}`}>
          {STATUS_LABEL[goal.status] ?? goal.status}
        </span>
        {goal.template_key && (
          <span className={styles.templateTag}>{goal.template_key.replace(/_/g, ' ')}</span>
        )}
      </div>
      <p className={styles.cardTitle}>{goal.title}</p>
      {goal.description && <p className={styles.cardDesc}>{goal.description}</p>}
      {cadence && (
        <div className={styles.cardSchedule}>
          <span className={styles.scheduleIcon}>⏰</span>
          <span>{cadence}{nextRun ? ` · next ${nextRun}` : ''}</span>
        </div>
      )}
      <div className={styles.cardMeta}>
        <span className={styles.metaItem}>Last run: {timeAgo(goal.last_run_at)}</span>
        {goal.current_value && (
          <span className={styles.metaItem}>Value: {goal.current_value}</span>
        )}
      </div>
      {canWrite && (
        <div className={styles.cardActions} onClick={e => e.stopPropagation()}>
          {confirming ? (
            <>
              <span className={styles.confirmText}>Archive this goal?</span>
              <button className={styles.actionDanger} onClick={() => onArchive(goal.id)}>Yes, archive</button>
              <button className={styles.actionCancel} onClick={() => setConfirming(false)}>Cancel</button>
            </>
          ) : (
            <button className={styles.actionSecondary} onClick={() => setConfirming(true)}>Archive</button>
          )}
        </div>
      )}
    </div>
  );
}

function ScheduleCard({ task, canWrite, onCancel }: {
  task: ScheduledTask; canWrite: boolean; onCancel: (id: string) => void;
}) {
  const [confirming, setConfirming] = useState(false);
  const nextRun = task.enabled ? formatTaskTime(task.next_run_at) : null;
  const statusClass = task.enabled
    ? styles.status_active
    : task.last_status === 'failed' ? styles.status_abandoned : styles.status_completed;
  const statusLabel = task.enabled
    ? 'Scheduled'
    : task.last_status === 'failed' ? 'Failed' : 'Done';

  return (
    <div className={styles.card}>
      <div className={styles.cardTop}>
        <span className={`${styles.statusBadge} ${statusClass}`}>{statusLabel}</span>
        <span className={styles.templateTag}>{formatTaskCadence(task)}</span>
      </div>
      <p className={styles.cardTitle}>{task.name}</p>
      <div className={styles.cardSchedule}>
        <span className={styles.scheduleIcon}>⏰</span>
        <span>
          {nextRun ? `Runs ${nextRun}` : `Last run ${timeAgo(task.last_run_at)}`}
        </span>
      </div>
      {canWrite && task.enabled && (
        <div className={styles.cardActions} onClick={e => e.stopPropagation()}>
          {confirming ? (
            <>
              <span className={styles.confirmText}>Cancel this task?</span>
              <button className={styles.actionDanger} onClick={() => onCancel(task.id)}>Yes, cancel</button>
              <button className={styles.actionCancel} onClick={() => setConfirming(false)}>Keep</button>
            </>
          ) : (
            <button className={styles.actionSecondary} onClick={() => setConfirming(true)}>Cancel</button>
          )}
        </div>
      )}
    </div>
  );
}

const RECENT_TASK_WINDOW_MS = 24 * 60 * 60 * 1000;

export default function GoalsTab() {
  const qc = useQueryClient();
  const canWrite = useCanWriteAgent();
  const [showCreate, setShowCreate] = useState(false);

  const { data: goals, isLoading, error } = useQuery({
    queryKey: ['goals'],
    queryFn: () => listGoals(),
    staleTime: 60_000,
  });

  // Poll schedules: the agent books one-shot follow-up checks mid-conversation
  // (e.g. "check the wordcloud job in 10 min") and they should appear here live.
  const { data: schedules } = useQuery({
    queryKey: ['schedules'],
    queryFn: () => listSchedules(),
    refetchInterval: 30_000,
  });

  async function handleArchive(goalId: string) {
    try {
      await archiveGoal(goalId);
      qc.invalidateQueries({ queryKey: ['goals'] });
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('Archive failed', e);
    }
  }

  async function handleCancelSchedule(taskId: string) {
    try {
      await deleteSchedule(taskId);
      qc.invalidateQueries({ queryKey: ['schedules'] });
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('Cancel schedule failed', e);
    }
  }

  const active = goals?.filter(g => g.status === 'active') ?? [];
  const other = goals?.filter(g => g.status !== 'active') ?? [];

  // Standalone schedules only — goal-bound ones already render as goal cards.
  const goalTaskIds = new Set((goals ?? []).map(g => g.scheduled_task_id).filter(Boolean));
  const standaloneTasks = (schedules ?? []).filter(t => !goalTaskIds.has(t.id));
  const upcomingTasks = standaloneTasks
    .filter(t => t.enabled)
    .sort((a, b) => (a.next_run_at ?? '').localeCompare(b.next_run_at ?? ''));
  const recentTasks = standaloneTasks
    .filter(t => !t.enabled && t.last_run_at
      && Date.now() - new Date(t.last_run_at).getTime() < RECENT_TASK_WINDOW_MS)
    .sort((a, b) => (b.last_run_at ?? '').localeCompare(a.last_run_at ?? ''))
    .slice(0, 5);
  const visibleTasks = [...upcomingTasks, ...recentTasks];

  return (
    <div className={styles.container}>
      <div className={styles.sectionHeader}>
        <p className={styles.sectionLabel}>Standing Goals</p>
        {canWrite && (
          <button className={styles.addBtn} onClick={() => setShowCreate(true)}>+ New Goal</button>
        )}
      </div>

      {isLoading && <p className={styles.emptyText}>Loading goals…</p>}
      {error && <p className={styles.errorText}>Could not load goals.</p>}

      {!isLoading && !error && active.length === 0 && other.length === 0 && visibleTasks.length === 0 && (
        <div className={styles.emptyState}>
          <span className={styles.emptyIcon}>🎯</span>
          <p className={styles.emptyText}>No standing goals yet.</p>
          <p className={styles.emptySubtext}>
            {canWrite
              ? 'Create a goal from a template and the agent will run it on a schedule automatically.'
              : 'An admin can create a goal from a template, and the agent will run it on a schedule automatically.'}
          </p>
          {canWrite && (
            <button className={styles.ctaBtn} onClick={() => setShowCreate(true)}>Get started</button>
          )}
        </div>
      )}

      {active.length > 0 && (
        <div className={styles.goalList}>
          {active.map(g => (
            <GoalCard key={g.id} goal={g} canWrite={canWrite} onArchive={handleArchive} />
          ))}
        </div>
      )}

      {visibleTasks.length > 0 && (
        <>
          <p className={`${styles.sectionLabel} ${styles.sectionLabelSpaced}`}>Scheduled Tasks</p>
          <div className={styles.goalList}>
            {visibleTasks.map(t => (
              <ScheduleCard key={t.id} task={t} canWrite={canWrite} onCancel={handleCancelSchedule} />
            ))}
          </div>
        </>
      )}

      {other.length > 0 && (
        <>
          <p className={`${styles.sectionLabel} ${styles.sectionLabelSpaced}`}>Archived</p>
          <div className={styles.goalList}>
            {other.map(g => (
              <GoalCard key={g.id} goal={g} canWrite={canWrite} onArchive={handleArchive} />
            ))}
          </div>
        </>
      )}

      {showCreate && (
        <GoalCreateModal
          onClose={() => setShowCreate(false)}
          onCreated={() => {
            setShowCreate(false);
            qc.invalidateQueries({ queryKey: ['goals'] });
          }}
        />
      )}
    </div>
  );
}
